<template>
  <div class="fill-height">
    <v-container fluid class="container-main">
      <v-row v-if="loading" justify="center" class="pt-8">
        <v-col cols="12" sm="10" md="8" lg="6">
          <v-card flat class="text-center pa-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <p class="text-body-1 mt-4 text--secondary">Loading your profile...</p>
          </v-card>
        </v-col>
      </v-row>

      <template v-else>
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8">
            <v-card class="pa-5 mb-4">
              <div class="d-flex align-center">
                <v-avatar color="primary" size="72" class="mr-4">
                  <span class="white--text text-h4">{{ initials }}</span>
                </v-avatar>
                <div class="flex-grow-1">
                  <h1 class="text-h5 mb-1">{{ displayName || 'Your Career Profile' }}</h1>
                  <p class="text-body-2 text--secondary mb-2">{{ location }}</p>
                  <div class="d-flex align-center">
                    <v-progress-linear
                      :value="completenessPercentage"
                      color="primary"
                      height="8"
                      rounded
                      style="max-width: 200px;"
                      class="mr-3"
                    ></v-progress-linear>
                    <span class="text-body-2 text--secondary">{{ completenessPercentage }}% complete</span>
                  </div>
                </div>
                <v-btn outlined @click="nav('/ProfileShowcase')" class="ml-2">
                  <v-icon small class="mr-1">mdi-eye</v-icon>
                  View Full Profile
                </v-btn>
              </div>
            </v-card>

            <v-row>
              <v-col cols="12" md="8">
                <v-card
                  class="pa-5 mb-4 primary"
                  dark
                  @click="nav('/SmallWinsCapture')"
                  style="cursor: pointer;"
                  elevation="4"
                >
                  <div class="d-flex align-center">
                    <v-avatar color="white" size="48" class="mr-4">
                      <v-icon color="primary">mdi-microphone</v-icon>
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="text-h6">Capture Something New</div>
                      <div class="text-body-2 opacity-80">
                        Record a win, skill, or note with voice or text
                      </div>
                    </div>
                    <v-icon>mdi-chevron-right</v-icon>
                  </div>
                </v-card>

                <v-card class="pa-4 mb-4" v-if="quickStats">
                  <h3 class="text-subtitle-1 mb-3">Your Career Evidence</h3>
                  <v-row>
                    <v-col cols="3" class="text-center">
                      <div class="text-h4 primary--text">{{ quickStats.skills }}</div>
                      <div class="text-caption text--secondary">Skills</div>
                    </v-col>
                    <v-col cols="3" class="text-center">
                      <div class="text-h4 primary--text">{{ quickStats.workExperience }}</div>
                      <div class="text-caption text--secondary">Jobs</div>
                    </v-col>
                    <v-col cols="3" class="text-center">
                      <div class="text-h4 primary--text">{{ quickStats.achievements }}</div>
                      <div class="text-caption text--secondary">Achievements</div>
                    </v-col>
                    <v-col cols="3" class="text-center">
                      <div class="text-h4 primary--text">{{ quickStats.smallWins }}</div>
                      <div class="text-caption text--secondary">Captures</div>
                    </v-col>
                  </v-row>
                </v-card>

                <v-card class="pa-4" v-if="recentWins.length > 0">
                  <div class="d-flex align-center justify-space-between mb-3">
                    <h3 class="text-subtitle-1 mb-0">Recent Activity</h3>
                    <v-btn x-small text color="primary" @click="nav('/SmallWinsProjects')">
                      View All
                    </v-btn>
                  </div>
                  <v-timeline dense align-top>
                    <v-timeline-item
                      v-for="win in recentWins.slice(0, 4)"
                      :key="win.id"
                      small
                      :color="getWinColor(win.parsedType)"
                    >
                      <div>
                        <div class="text-body-2">{{ truncate(win.rawInput, 80) }}</div>
                        <div class="text-caption text--secondary">
                          {{ formatDate(win.createdAt) }}
                          <v-chip x-small class="ml-1">{{ win.parsedType }}</v-chip>
                        </div>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                </v-card>
              </v-col>

              <v-col cols="12" md="4">
                <v-card class="pa-4 mb-4">
                  <h3 class="text-subtitle-1 mb-3">Quick Actions</h3>
                  <v-list dense class="py-0">
                    <v-list-item @click="nav('/Sage')" class="px-0">
                      <v-list-item-icon class="mr-3">
                        <v-icon color="secondary">mdi-chat-processing</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Tell Your Story</v-list-item-title>
                        <v-list-item-subtitle>Guided conversation</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="nav('/information')" class="px-0">
                      <v-list-item-icon class="mr-3">
                        <v-icon color="info">mdi-pencil</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Edit Profile</v-list-item-title>
                        <v-list-item-subtitle>Update your details</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="nav('/CareerStoryGenerator')" class="px-0">
                      <v-list-item-icon class="mr-3">
                        <v-icon color="warning">mdi-auto-fix</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Career Story</v-list-item-title>
                        <v-list-item-subtitle>AI-generated narrative</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="nav('/ResumeReview')" class="px-0">
                      <v-list-item-icon class="mr-3">
                        <v-icon color="success">mdi-file-document</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Export Resume</v-list-item-title>
                        <v-list-item-subtitle>Download or share</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>

                <v-card class="pa-4" v-if="incompleteItems.length > 0">
                  <h3 class="text-subtitle-1 mb-3">Strengthen Your Profile</h3>
                  <div>
                    <v-chip
                      v-for="item in incompleteItems"
                      :key="item.item"
                      small
                      outlined
                      color="warning"
                      class="mr-1 mb-2"
                      @click="navigateToSection(item.item)"
                      style="cursor: pointer;"
                    >
                      <v-icon x-small left>mdi-plus</v-icon>
                      {{ item.item }}
                    </v-chip>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>
</template>

<script>
import { GetProfileContext } from "@/api/ProfileContextApi";
import { mapFields } from "vuex-map-fields";

export default {
  name: "ProfileHome",
  data() {
    return {
      loading: true,
      displayName: "",
      location: "",
      completeness: null,
      quickStats: null,
      recentWins: []
    };
  },
  computed: {
    ...mapFields(["resumeId"]),
    initials() {
      if (!this.displayName) return "?";
      const parts = this.displayName.split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return this.displayName.substring(0, 2).toUpperCase();
    },
    completenessPercentage() {
      return this.completeness?.percentage || 0;
    },
    incompleteItems() {
      if (!this.completeness || !this.completeness.items) return [];
      return this.completeness.items.filter(item => !item.complete);
    }
  },
  async mounted() {
    await this.loadProfile();
  },
  methods: {
    async loadProfile() {
      try {
        const result = await GetProfileContext(this.resumeId);
        if (result.success && result.data) {
          this.displayName = result.data.displayName || "";
          this.location = result.data.location || "";
          this.completeness = result.data.completeness;
          this.quickStats = result.data.counts;
          this.recentWins = result.data.smallWins || [];
        }
      } catch (e) {
        console.error("ProfileHome load error:", e);
      } finally {
        this.loading = false;
      }
    },
    navigateToSection(sectionName) {
      const routes = {
        "Name": "/information",
        "Contact Info": "/information",
        "Skills": "/hardskills",
        "Work Experience": "/Work",
        "Education": "/education",
        "Achievements": "/SmallWinsCapture"
      };
      this.nav(routes[sectionName] || "/information");
    },
    getWinColor(type) {
      const colors = {
        achievement: "success",
        skill: "info",
        note: "grey",
        project: "warning"
      };
      return colors[type] || "grey";
    },
    truncate(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    },
    nav(route) {
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.opacity-80 {
  opacity: 0.8;
}
</style>
